import { getTopBarValues } from "@/controllers/instruments";
import { getStockInfoOnClient } from "@/controllers/stock";
import { tickerTapeState, userState } from "@/lib/store";
import { Box } from "@mui/material";
import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import Ticker from "react-ticker";
import { useRecoilState, useRecoilValue } from "recoil";
import styles from "./NewTickerTape.module.scss";
import TickerStock from "./TickerStock";
import TickertapePopover from "../tickertape-popover/TickertapePopover";
import { Context } from "@/lib/Context";
import { unsuscribeFromWS } from "@/lib/utils";
import { useTickerTapeFeed } from "src/hooks/useTickerTapeFeed";

const tickertapeRowSx = { width: "100%", display: "flex", alignItems: "flex-start" } as const;

const liveInstruments: { [identifier: string]: { name: string; currency?: string } } = {
    "INDU:IND": { name: "DJIA" },
    "SPX:IND": { name: "S&P 500" },
    "CCMP:IND": { name: "Nasdaq Comp" },
    "US100:IND": { name: "Nasdaq 100" },
    "CL1:COM": { name: "OIL (WTI)", currency: "COM" },
    "SX5E:IND": { name: "ESTOXX50", currency: "COM" },
};

const NewTickerTape: React.FC = ({}) => {
    const [pause, setPause] = useState<boolean>(false);
    const [instrumentClicked, setInstrumentClicked] = useState(null);
    const [anchorEl, setAnchorEl] = useState<any>(null);
    const { multiWebSocket, ortexWebSocket } = useContext(Context);

    const [tickerStocks, setTickerStocks] = useRecoilState(tickerTapeState);
    const prevTickerStocksRef = useRef<typeof tickerStocks>({});
    const isMountedRef = useRef(true);
    const user = useRecoilValue(userState);

    const instrumentsList = useMemo(() => {
        if (!tickerStocks) return [];
        return Object.entries(tickerStocks)
            .filter(([key, instrument]) => key !== "undefined" && instrument.visibility && !instrument.removed)
            .sort(([, instrumentA], [, instrumentB]) => {
                const orderA = instrumentA.order !== undefined ? instrumentA.order : Infinity;
                const orderB = instrumentB.order !== undefined ? instrumentB.order : Infinity;
                return orderA - orderB;
            });
    }, [tickerStocks]);

    // Centralized WebSocket feed: subscribes each instrument once and routes
    // every socket message through a single parse, instead of one listener per
    // rendered (and chain-duplicated) ticker item. Leaves read live data by
    // subscribing to this store (react-ticker freezes the element tree, so data
    // cannot flow down through props).
    const feedStore = useTickerTapeFeed(instrumentsList);

    // Remount the <Ticker> only when the set/order of instruments changes (so
    // chain mode re-measures its width), never on price ticks.
    const tapeKey = useMemo(() => instrumentsList.map(([key]) => key).join("|"), [instrumentsList]);

    const getLiveInstruments = useCallback(() => {
        getTopBarValues()
            .then((values) => {
                if (!isMountedRef.current) return; // Don't update state if component unmounted

                const newInstruments: any = {};
                Object.keys(values)
                    .filter((key) => key !== "obfuscated" && liveInstruments[key])
                    .forEach((key) => {
                        newInstruments[key] = {
                            name: liveInstruments[key].name,
                            currency: liveInstruments[key].currency,
                            price: values[key].price,
                            lastPrice: values[key].price,
                            lastChange: values[key].price,
                            timestamp: values[key].ts,
                            visibility: true,
                            percentage: typeof values[key]?.nch === "number" && typeof values[key]?.price === "number" && values[key]?.price !== 0 ? (values[key].nch / values[key].price) * 100 : 0,
                        };
                    });

                setTickerStocks((prevState) => ({ ...prevState, ...newInstruments }));
            })
            .catch((err) => {
                if (isMountedRef.current) {
                    console.error("Error getting live instruments", err);
                }
            });
    }, [setTickerStocks]);

    useEffect(() => {
        if (!user?.settings) return;

        const savedTickerStocks = user?.settings?.module_settings["tickerTape->tickertape-settings"] ? Object.entries(user?.settings?.module_settings["tickerTape->tickertape-settings"]) : [];

        const handleSavedTickerStock = (identifier: string, instrument: any) => {
            if (!liveInstruments[identifier] && identifier !== "BTCUSD" && identifier !== "undefined" && identifier !== "WTIUSDBCOMP" && identifier !== "NDX:IND") {
                getStockInfoOnClient(instrument.stock_id)
                    .then((data) => {
                        if (!isMountedRef.current) return; // Don't update state if component unmounted
                        setTickerStocks((prevState) => ({
                            ...prevState,
                            [identifier]: {
                                ...instrument,
                                isin: data.isin,
                                price: data.last_close,
                                lastPrice: data.last_close,
                                lastChange: 0,
                            },
                        }));
                    })
                    .catch((err) => {
                        if (isMountedRef.current) {
                            console.error("Error getting stock info", err);
                        }
                    });
            }
        };

        if (savedTickerStocks.length > 0) {
            for (let i = 0; i < savedTickerStocks.length; i++) {
                const [identifier, instrument] = savedTickerStocks[i];
                handleSavedTickerStock(identifier, instrument);
            }
            getLiveInstruments();
        } else {
            getLiveInstruments();
        }
    }, [user?.settings?.module_settings["tickerTape->tickertape-settings"], getLiveInstruments, setTickerStocks]);

    useEffect(() => {
        try {
            const prevStocks = prevTickerStocksRef.current;
            const currentStocks = tickerStocks || {};

            // Find removed tickers by comparing previous and current
            const removedTickers = Object.keys(prevStocks).filter((ticker) => !currentStocks.hasOwnProperty(ticker));

            removedTickers.forEach((ticker) => {
                const stock = prevStocks[ticker];
                if (!stock || stock.name === "Bitcoin") return;

                // unsuscribeFromWS expects [identifier, stock] format
                unsuscribeFromWS([ticker, stock], multiWebSocket, ortexWebSocket);
            });

            // Update ref with current stocks for next comparison
            prevTickerStocksRef.current = { ...currentStocks };
        } catch (error) {
            console.error("Error when unsubscribing from WS", error);
        }
    }, [tickerStocks, multiWebSocket, ortexWebSocket]);

    // Cleanup: Unsubscribe from all WebSocket subscriptions on unmount
    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
            // Unsubscribe from all ticker stocks on unmount
            const currentStocks = prevTickerStocksRef.current || {};
            Object.entries(currentStocks).forEach(([ticker, stock]: [string, any]) => {
                if (stock && stock.name !== "Bitcoin") {
                    try {
                        unsuscribeFromWS([ticker, stock], multiWebSocket, ortexWebSocket);
                    } catch (error) {
                        console.error("Error unsubscribing on unmount", error);
                    }
                }
            });
        };
    }, [multiWebSocket, ortexWebSocket]);

    const handleResumePlaying = () => {
        setInstrumentClicked(null);
        setPause(false);
        setAnchorEl(null);
    };

    const handleTickerClick = (e, instrument) => {
        setInstrumentClicked(instrument);
        setAnchorEl(e.currentTarget);
        setPause(true);
    };

    return (
        <div className={styles.tickerwrap}>
            {instrumentsList.length > 0 && (
                <div className={styles.tickerFadeIn}>
                    <Ticker key={`tickertape-${tapeKey}`} mode="chain" move={!pause} speed={3} offset={0}>
                        {({ index }) => (
                            <Box className={styles.tickertape} sx={tickertapeRowSx}>
                                {instrumentsList.map(([key]) => (
                                    <TickerStock store={feedStore} tickerKey={key} handleTickerClick={handleTickerClick} key={`${index}-${key}`} />
                                ))}
                            </Box>
                        )}
                    </Ticker>
                </div>
            )}
            <TickertapePopover instrument={instrumentClicked} anchorEl={anchorEl} handleClose={handleResumePlaying} />
        </div>
    );
};

export default NewTickerTape;
